import React, { useCallback, useMemo, useState } from "react";
import { Dialog, DialogContent } from "@src/lib/ui/Dialog";
import { useChatContext } from "@src/features/assistantChat/ChatRuntimeProvider";
import { useChatSidebar } from "@src/features/aiAssist/hooks/useChatSidebar";
import { marked } from "marked";
import { AIAssistBrandSelector } from "@src/features/aiAssist/hooks/useAIAssistBrandSelector";
import { ChatAttachmentItem } from "@src/features/aiAssist/uiComponents/ChatAttachment";
import ChatCanvas, {
  ChatCanvasProps,
} from "@src/features/aiAssist/uiComponents/ChatCanvas";
import { transformToSelectedSession } from "@src/features/aiAssist/utils/sessionTransform";
import Sparkles2FillIcon from "@src/lib/icons/18px/sparkles-2-fill";
import { AiChatType } from "@src/types.generated";

type Props = {
  open: boolean;
  chatType: AiChatType;
  setDialogIsOpen: (open: boolean) => void;
  actionOnClick?: () => void;
  actionLabel?: string;
  actionLoading?: boolean;
  canvasSubtitle: string;
  threadEmptyStateTitle: string;
  threadEmptyStateSubtitle?: string;
  composerPlaceholder: string;
  brandSelectorProps: AIAssistBrandSelector;
  showConversionProgress?: boolean;
  threadActionButtonHandlers?: {
    [key: string]: () => void;
  };
  chatSidebarTitle?: string;
};

const ChatCanvasDialogContainer: React.FC<Props> = ({
  open,
  chatType,
  setDialogIsOpen,
  actionOnClick,
  actionLabel,
  actionLoading,
  canvasSubtitle,
  threadEmptyStateTitle,
  threadEmptyStateSubtitle,
  composerPlaceholder,
  brandSelectorProps,
  showConversionProgress = false,
  threadActionButtonHandlers,
  chatSidebarTitle,
}) => {
  const { messages, submitMessage, status, stop } = useChatContext();
  const [composerTextInput, setComposerTextInput] = useState("");
  const [attachments, setAttachments] = useState<ChatAttachmentItem[]>([]);

  const {
    startNewConversation,
    selectSession,
    selectedSession,
    setArtifact,
    setArtifactMessageId,
  } = useChatSidebar(chatType);

  const canvasContent = selectedSession?.artifact || "";

  const handleSelectCanvas = useCallback(
    (markdownCanvasContent: string) => {
      const html = marked.parse(markdownCanvasContent);
      if (typeof html === "string") {
        setArtifact(html);
        const messageWithCanvas = messages.find((message) =>
          message.content.includes(markdownCanvasContent),
        );

        if (messageWithCanvas) {
          setArtifactMessageId(messageWithCanvas.id);
        }
      }
    },
    [messages, setArtifact, setArtifactMessageId],
  );

  const {
    brandSelectOptions,
    selectedAIBrandContextId: selectedBrandId,
    setSelectedAIBrandContextId: setSelectedBrandId,
    selectedBrandItem,
  } = brandSelectorProps;
  const handleChatSubmit = useCallback(() => {
    setComposerTextInput("");
    setAttachments([]);
    submitMessage({
      role: "user",
      content: composerTextInput,
      customMessageData: {
        brandDetailsId: selectedBrandId,
        attachments: [...attachments, { canvasContent: canvasContent }],
      },
    });
  }, [
    composerTextInput,
    submitMessage,
    selectedBrandId,
    attachments,
    canvasContent,
  ]);
  const chatCanvasProps: ChatCanvasProps = useMemo(
    () => ({
      canvasProps: {
        canvasHtmlContent: canvasContent,
        onCanvasContentChange: setArtifact,
        subtitle: canvasSubtitle,
        onAction: actionOnClick,
        onActionLoading: actionLoading,
        actionLabel: actionLabel,
        actionIcon: <Sparkles2FillIcon className="size-4" />,
        showConversionProgress,
      },
      chatSidebarProps: {
        headerProps: {
          title: chatSidebarTitle,
          onClickNewChat: () => {
            startNewConversation();
            setArtifact("");
            setArtifactMessageId(null);
          },
          onClickClose: () => {
            if (!selectedSession) {
              setArtifact("");
              setArtifactMessageId(null);
            }
            setDialogIsOpen(false);
          },
          chatHistoryProps: {
            filters: {
              chatType: chatType,
            },
            onClickPreviousChat: (x) => {
              const selectedSession = transformToSelectedSession(x);
              selectSession(selectedSession);
            },
          },
        },
        threadProps: {
          messages,
          isStreaming: status === "streaming",
          emptyStateTitle: threadEmptyStateTitle,
          emptyStateSubtitle: threadEmptyStateSubtitle,
          onSelectCanvas: handleSelectCanvas,
          threadActionButtonHandlers,
          chatAvatarName: "Opus AI",
        },
        composerProps: {
          input: composerTextInput,
          onInputChange: setComposerTextInput,
          onSubmit: handleChatSubmit,
          isStreaming: status === "streaming",
          onStop: stop,
          placeholder: composerPlaceholder,
          disabled: showConversionProgress,
          brandSelectorProps: {
            brandSelectOptions,
            onSelectValueChange: setSelectedBrandId,
            selectedBrandItem,
            defaultValue: selectedBrandId ?? "",
          },
          attachmentProps: {
            enableAttachments: true,
            attachments,
            onAttachmentsChanged: setAttachments,
          },
        },
      },
    }),
    [
      canvasContent,
      setArtifact,
      setArtifactMessageId,
      canvasSubtitle,
      actionOnClick,
      actionLoading,
      actionLabel,
      showConversionProgress,
      messages,
      status,
      threadEmptyStateTitle,
      threadEmptyStateSubtitle,
      composerTextInput,
      handleChatSubmit,
      stop,
      composerPlaceholder,
      brandSelectOptions,
      setSelectedBrandId,
      selectedBrandItem,
      selectedBrandId,
      attachments,
      startNewConversation,
      selectedSession,
      setDialogIsOpen,
      selectSession,
      handleSelectCanvas,
      chatType,
      threadActionButtonHandlers,
      chatSidebarTitle,
    ],
  );

  return (
    <Dialog open={open} onOpenChange={setDialogIsOpen}>
      <DialogContent
        className="mx-[24px] flex h-[90vh] max-h-[90vh] w-[90vw] max-w-[1400px] gap-0 overflow-hidden p-0 [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <ChatCanvas {...chatCanvasProps} />
      </DialogContent>
    </Dialog>
  );
};

export default ChatCanvasDialogContainer;
